import { Phone, Ruler, Palette, Hammer, Truck } from "lucide-react";
import { motion } from "framer-motion";
import { DotGrid, NumberWatermark, FloatingShape } from "@/components/Deco";

const STEPS = [
  {
    n: "01",
    icon: Phone,
    title: "Kapcsolatfelvétel",
    text: "Hívjon vagy küldje el ajánlatkérését – gyorsan visszahívjuk.",
  },
  {
    n: "02",
    icon: Ruler,
    title: "Méretvétel",
    text: "Pontos méretvétel a helyszínen vagy egyeztetés alapján.",
  },
  {
    n: "03",
    icon: Palette,
    title: "Anyag- és színválasztás",
    text: "Mintákból válogatva segítünk a megfelelő anyag és szín kiválasztásában.",
  },
  {
    n: "04",
    icon: Hammer,
    title: "Gyártás vagy javítás",
    text: "Műhelyünkben hegesztéssel, profi gépparkkal készül a ponyva.",
  },
  {
    n: "05",
    icon: Truck,
    title: "Átadás",
    text: "Felszerelés és tanácsadás.",
  },
];

export const Process = () => {
  return (
    <section
      id="munkafolyamat"
      data-testid="process-section"
      className="section-pad bg-white relative overflow-hidden"
    >
      <FloatingShape className="-top-32 -right-32 w-96 h-96" color="#EA580C" />
      <DotGrid className="absolute bottom-10 right-10 w-72 h-32 opacity-50" size={14} />
      <NumberWatermark n="03" className="hidden md:block top-12 -right-12" />
      <div className="max-w-7xl mx-auto px-4 md:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.3 }}
          transition={{ duration: 0.6 }}
          className="max-w-2xl"
        >
          <span className="eyebrow">Munkafolyamat</span>
          <h2 className="mt-3 font-heading font-bold text-[#0B192C] text-3xl sm:text-4xl lg:text-5xl tracking-tight">
            Öt lépés a kész ponyváig.
          </h2>
        </motion.div>

        {/* Animated horizontal line */}
        <div className="mt-16 relative hidden lg:block">
          <div className="absolute top-7 left-[5%] right-[5%] h-0.5 bg-gray-200" />
          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 1.6, ease: "easeOut" }}
            style={{ transformOrigin: "left" }}
            className="absolute top-7 left-[5%] right-[5%] h-0.5 bg-gradient-to-r from-[#EA580C] via-[#EA580C] to-[#1E3E62]"
          />
        </div>

        <div className="mt-2 lg:mt-0 grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-y-12 md:gap-x-6">
          {STEPS.map((step, i) => (
            <motion.div
              key={step.n}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.3 }}
              transition={{ duration: 0.5, delay: i * 0.12 }}
              data-testid={`process-step-${step.n}`}
              className="relative flex flex-col items-start text-left group"
            >
              <div className="relative">
                <div className="w-14 h-14 rounded-full bg-[#1E3E62] text-white flex items-center justify-center shadow-lg group-hover:bg-[#EA580C] group-hover:scale-110 transition-all duration-300 z-10 relative">
                  <step.icon className="w-6 h-6" />
                </div>
                <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-[#EA580C] text-white text-xs font-bold flex items-center justify-center font-heading shadow-md z-20">
                  {step.n}
                </span>
                <span className="absolute inset-0 rounded-full border-2 border-[#EA580C]/40 ping-soft" />
              </div>
              <h3 className="mt-5 font-heading font-bold text-[#0B192C] text-lg">
                {step.title}
              </h3>
              <p className="mt-2 text-sm text-gray-600 leading-relaxed">
                {step.text}
              </p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Process;
